/**
 * Rename a conversation — `PATCH /api/chat/sessions/:id { title }`. An in-app
 * dialog, not `window.prompt`: Electron doesn't implement `prompt()` (it
 * throws), so a prompt-based rename never worked in the desktop app.
 */
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { FormDialog } from '@onestone/ui/form-dialog';
import { Input } from '@onestone/ui/input';
import { Label } from '@onestone/ui/label';
import { api, mutationError } from '@/lib/api';
import type { ConversationSummary } from '@/lib/chatStore';

export function RenameConversationDialog({
  conversation,
  onClose,
  onRenamed,
}: {
  /** The conversation to rename; null keeps the dialog closed. */
  conversation: ConversationSummary | null;
  onClose: () => void;
  onRenamed: () => void;
}): JSX.Element {
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');

  const rename = useMutation({
    mutationFn: (args: { id: string; title: string }) =>
      api(`/api/chat/sessions/${encodeURIComponent(args.id)}`, { method: 'PATCH', json: { title: args.title } }),
    onSuccess: () => {
      onRenamed();
      onClose();
    },
    onError: (err) => setError(mutationError(err, 'Could not rename the conversation')),
  });

  return (
    <FormDialog
      open={conversation !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onReset={() => {
        setTitle(conversation?.title ?? conversation?.model ?? '');
        setError('');
      }}
      title="Rename conversation"
      submitLabel="Rename"
      pending={rename.isPending}
      error={error}
      onSubmit={() => {
        if (!conversation) return;
        if (!title.trim()) {
          setError('A title is required');
          return;
        }
        rename.mutate({ id: conversation.id, title: title.trim() });
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="rename-conversation-title">Title</Label>
        <Input
          id="rename-conversation-title"
          className="h-9"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
    </FormDialog>
  );
}
